import { localizePath as __lp } from "@/i18n/locales";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Plug,
  ShieldCheck,
  RefreshCw,
  Workflow,
  MessageSquare,
  Users,
  ShoppingCart,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FAQSection from "@/components/FAQSection";
import ToolLogo from "@/components/ToolLogo";
import IntegrationOrbit from "@/components/IntegrationOrbit";
import { ScrollReveal } from "@/components/ScrollReveal";
import { integrationCategories } from "@/data/integrations";
import { useLocale } from "@/i18n/LocaleProvider";
import { LANG_HREFLANG } from "@/i18n/locales";

const categoryIcon: Record<string, LucideIcon> = {
  communication: MessageSquare,
  sales: Users,
  commerce: ShoppingCart,
  productivity: LayoutGrid,
};

const stepIcon: LucideIcon[] = [Plug, Workflow, ShieldCheck, RefreshCw];

const Integrations = () => {
  const { t } = useTranslation();
  const { lang, localize } = useLocale();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = t("integrations.steps", { returnObjects: true }) as {
    title: string;
    body: string;
  }[];
  const faqItems = t("integrations.faqs", { returnObjects: true }) as {
    q: string;
    a: string;
  }[];
  const stats = t("integrations.stats", { returnObjects: true }) as {
    value: string;
    label: string;
  }[];

  const SITE = "https://devcore.systems";
  const langPath = lang === "en" ? "" : `/${lang}`;
  const canonical = `${SITE}${__lp("/integrations", lang)}`;

  const jsonLd = useMemo(() => {
    const organization = {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "DevCore",
      url: `${SITE}/`,
      logo: { "@type": "ImageObject", url: `${SITE}/favicon.png` },
      sameAs: ["https://www.devcore.systems", "https://www.linkedin.com/company/devcore-websystems"],
    };
    return [
      organization,
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        name: t("integrations.metaTitle"),
        description: t("integrations.metaDescription"),
        url: canonical,
        inLanguage: LANG_HREFLANG[lang],
        breadcrumb: { "@id": `${canonical}#breadcrumb` },
        about: { "@id": `${canonical}#service` },
        publisher: { "@id": `${SITE}/#organization` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("leadPage.home"), item: `${SITE}${langPath}/` },
          { "@type": "ListItem", position: 2, name: t("integrations.eyebrow"), item: canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: t("integrations.metaTitle"),
        serviceType: "Custom software integration development",
        description: t("integrations.metaDescription"),
        url: canonical,
        provider: { "@id": `${SITE}/#organization` },
        areaServed: { "@type": "Place", name: "Worldwide" },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: t("integrations.toolsTitle"),
          itemListElement: integrationCategories.map((cat) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: t(`integrations.categories.${cat.id}.title`),
              description: t(`integrations.categories.${cat.id}.desc`),
            },
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [t, lang, canonical, langPath, faqItems]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("integrations.metaTitle")}
        description={t("integrations.metaDescription")}
        canonical={canonical}
        ogType="website"
        ogImage="https://devcore.systems/og-image.png"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 tech-grid pointer-events-none opacity-[0.12]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-1 left-[-10%] top-[-10%] h-[55vw] w-[55vw]" />
          <div className="aurora-blob aurora-2 right-[-15%] top-[10%] h-[50vw] w-[50vw]" />
        </div>

        <div className="container relative z-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <li>
                <Link to={localize("/")} className="transition-colors hover:text-accent">
                  {t("leadPage.home")}
                </Link>
              </li>
              <li aria-hidden className="text-border">/</li>
              <li className="text-foreground">{t("integrations.eyebrow")}</li>
            </ol>
          </nav>

          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,540px)] lg:gap-8">
          <ScrollReveal className="max-w-4xl">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-1.5 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
              <span className="mono-label text-muted-foreground">// {t("integrations.eyebrow")}</span>
            </div>
            <h1 className="text-[1.9rem] sm:text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-7xl">
              {t("integrations.title")}{" "}
              <span className="gradient-text">{t("integrations.titleAccent")}</span>
            </h1>
            <p className="mt-7 max-w-2xl text-[15px] sm:text-lg leading-relaxed text-muted-foreground md:text-xl">
              {t("integrations.subtitle")}
            </p>

            <div className="mt-10 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
              <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                <Link
                  to={localize("/contact")}
                  className="group inline-flex w-full items-center justify-center gap-2 btn-clip bg-primary px-8 py-4 text-lg font-semibold text-primary-foreground btn-label-contrast transition-transform duration-200 active:scale-[0.97] sm:w-auto"
                >
                  {t("integrations.heroPrimaryCta")}
                  <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </span>
              <a
                href="#integration-tools"
                className="inline-flex w-full items-center justify-center gap-2 btn-clip bg-card/40 px-8 py-4 text-lg font-semibold text-foreground transition-transform duration-200 active:scale-[0.97] sm:w-auto"
              >
                <Plug size={18} className="text-accent" />
                {t("integrations.heroSecondaryCta")}
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150} className="relative">
            <IntegrationOrbit coreLabel={t("integrations.teaser.core")} />
          </ScrollReveal>
          </div>

          <ScrollReveal delay={150} className="mt-16 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-xl border border-border bg-card/40 p-6 text-center backdrop-blur-sm">
                <p className="font-heading text-[1.9rem] sm:text-4xl font-bold gradient-text md:text-5xl">{stat.value}</p>
                <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </header>

      {/* ===== TOOL CATEGORIES ===== */}
      <section id="integration-tools" className="relative border-t border-border py-14 sm:py-20 md:py-28">
        <div className="container">
          <ScrollReveal className="mb-14 max-w-2xl">
            <p className="mono-label mb-4">{t("integrations.toolsKicker")}</p>
            <h2 className="text-[1.55rem] sm:text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl lg:text-5xl">
              {t("integrations.toolsTitle")}
            </h2>
          </ScrollReveal>

          <div className="grid gap-6 lg:grid-cols-2">
            {integrationCategories.map((cat, i) => {
              const Icon = categoryIcon[cat.id] ?? Plug;
              return (
                <ScrollReveal key={cat.id} delay={i * 90}>
                  <article className="h-full rounded-2xl border border-border bg-card/40 p-7 backdrop-blur-sm transition-colors duration-300 hover:border-accent/40">
                    <div className="flex items-start gap-4">
                      <span className="inline-flex shrink-0 rounded-xl border border-border bg-background/60 p-3 text-accent">
                        <Icon size={22} />
                      </span>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">
                          {t(`integrations.categories.${cat.id}.title`)}
                        </h3>
                        <p className="mt-2 text-muted-foreground leading-relaxed">
                          {t(`integrations.categories.${cat.id}.desc`)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-7 flex flex-wrap gap-3">
                      {cat.tools.map((tool) => (
                        <div
                          key={tool.domain}
                          className="group/logo flex h-14 items-center gap-3 rounded-xl border border-border bg-background/50 px-4 transition-colors duration-300 hover:border-accent/40"
                        >
                          <ToolLogo name={tool.name} domain={tool.domain} size={64} className="h-7 max-w-[88px]" />
                          <span className="text-sm font-medium text-foreground/80">{tool.name}</span>
                        </div>
                      ))}
                    </div>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>

          <ScrollReveal delay={120}>
            <p className="mt-8 text-center text-sm text-muted-foreground">{t("integrations.toolsNote")}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative overflow-hidden border-t border-border bg-secondary/20 py-14 sm:py-20 md:py-28">
        <div className="absolute inset-0 tech-grid pointer-events-none opacity-[0.1]" />
        <div className="container relative z-10">
          <ScrollReveal className="mb-14 max-w-2xl">
            <p className="mono-label mb-4">{t("integrations.processKicker")}</p>
            <h2 className="text-[1.55rem] sm:text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl lg:text-5xl">
              {t("integrations.processTitle")}
            </h2>
          </ScrollReveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, i) => {
              const Icon = stepIcon[i] ?? Plug;
              return (
                <ScrollReveal key={step.title} delay={i * 90}>
                  <div className="relative h-full rounded-2xl border border-border bg-card/40 p-6 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex rounded-xl border border-border bg-background/60 p-2.5 text-accent">
                        <Icon size={20} />
                      </span>
                      <span className="font-mono text-sm text-accent/60">0{i + 1}</span>
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <FAQSection
        id="integrations-faq"
        label={t("integrations.faqKicker")}
        headingA={t("integrations.faqTitle")}
        items={faqItems.map((f) => ({ question: f.q, answer: f.a }))}
      />

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden border-t border-border bg-secondary/30 py-16 sm:py-24 md:py-32">
        <div className="absolute inset-0 tech-dots pointer-events-none opacity-[0.15]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-3 left-1/2 top-1/2 h-[60vw] w-[60vw] -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="container relative z-10 text-center">
          <ScrollReveal className="mx-auto max-w-2xl">
            <h2 className="text-[1.55rem] sm:text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl lg:text-5xl">
              {t("integrations.ctaTitle")}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] sm:text-lg leading-relaxed text-muted-foreground">
              {t("integrations.ctaBody")}
            </p>
            <div className="mt-9 flex justify-center">
              <span className="hover-glow-accent inline-block">
                <Link
                  to={localize("/contact")}
                  className="group inline-flex items-center gap-2 btn-clip bg-primary px-8 py-4 text-lg font-semibold text-primary-foreground btn-label-contrast transition-transform duration-200 active:scale-[0.97]"
                >
                  {t("integrations.ctaPrimary")}
                  <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </span>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Integrations;
